import { HttpClient } from './httpClient';
import { enableConsoleLogs } from './debugFlags';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  GoogleLoginRequest,
  GoogleLoginInitiateResponse,
  GoogleCallbackRequest,
  GoogleCallbackResponse,
  ForgotPasswordRequest,
  ResetPasswordCompleteRequest,
  ApiResponse,
} from '@passport/api-types';

// Login/sign-in flow diagnostic logging (login, verifyToken, issueHandoffCode, redeemHandoffCode
// below) — gated behind enableConsoleLogs.loginProcess. DO NOT remove these console.log
// statements in future changes; toggle the flag instead. See debugFlags.ts for why.

export interface VerifyMfaRequest {
  userId: number;
  code: string;
  deviceId?: string;
  deviceName?: string;
  ipAddress?: string;
  userAgent?: string;
}

// Default API endpoints
const DEFAULT_ENDPOINTS = {
  login: '/api/v1/iam/auth/login',
  signup: '/api/v1/iam/auth/register',
  logout: '/api/v1/iam/auth/logout',
  refreshToken: '/api/v1/iam/auth/refresh-token',
  verifyToken: '/api/v1/iam/auth/verify-token',
  changePassword: '/api/v1/iam/auth/change-password',
  verifyMfa: '/api/v1/iam/auth/verify-mfa',
  googleInitiate: '/api/authentication/google/login',
  googleCallback: '/api/authentication/google/callback',
  forgotPassword: '/api/v1/iam/auth/forgot-password',
  resetPassword: '/api/v1/iam/auth/reset-password/complete',
  issueHandoffCode: '/api/v1/iam/auth/handoff/issue',
  redeemHandoffCode: '/api/v1/iam/auth/handoff/redeem',
};

export interface IssueHandoffCodeResponse {
  code: string;
}

export interface RedeemHandoffCodeResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

/**
 * Centralized authentication API client
 * Uses standard endpoints (can be overridden via constructor parameter)
 */
export class AuthClient {
  constructor(
    private http: HttpClient,
    private endpoints = DEFAULT_ENDPOINTS
  ) {}

  async login(request: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    if (enableConsoleLogs.loginProcess) {
      console.log('[AuthClient.login] Submitting credentials', {
        url: this.endpoints.login,
        method: 'POST',
        email: request.email,
        // Password intentionally omitted from the log.
      });
    }
    try {
      // Not given an explicit generic here (same as before logging was added) — the declared
      // Promise<ApiResponse<LoginResponse>> return type below lets it contextually infer
      // T = ApiResponse<LoginResponse>, matching every existing caller's `(response as
      // any).data ?? response` double-unwrap defense elsewhere in this codebase.
      const response = await this.http.post(this.endpoints.login, request);
      if (enableConsoleLogs.loginProcess) {
        console.log('[AuthClient.login] Response received', {
          url: this.endpoints.login,
          success: (response as any)?.success,
          hasAccessToken: !!(response as any)?.data?.accessToken || !!(response as any)?.accessToken,
          mfaRequired: !!(response as any)?.data?.mfaRequired || !!(response as any)?.mfaRequired,
        });
      }
      return response as ApiResponse<LoginResponse>;
    } catch (error) {
      if (enableConsoleLogs.loginProcess) {
        console.log('[AuthClient.login] Request FAILED', {
          url: this.endpoints.login,
          error,
        });
      }
      throw error;
    }
  }

  async register(request: RegisterRequest): Promise<ApiResponse<{ id: string }>> {
    return this.http.post(this.endpoints.signup, request);
  }

  async refreshToken(request: RefreshTokenRequest): Promise<ApiResponse<RefreshTokenResponse>> {
    return this.http.post(this.endpoints.refreshToken, request);
  }

  async verifyMfa(request: VerifyMfaRequest): Promise<ApiResponse<LoginResponse>> {
    return this.http.post(this.endpoints.verifyMfa, request);
  }

  async googleLogin(request: GoogleLoginRequest): Promise<ApiResponse<GoogleLoginInitiateResponse>> {
    return this.http.post(this.endpoints.googleInitiate, request);
  }

  async googleCallback(request: GoogleCallbackRequest): Promise<ApiResponse<GoogleCallbackResponse>> {
    const params = new URLSearchParams();
    Object.entries(request).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    return this.http.get(`${this.endpoints.googleCallback}?${params.toString()}`);
  }

  async verifyToken(): Promise<ApiResponse<{ valid: boolean }>> {
    if (enableConsoleLogs.loginProcess) {
      console.log('[AuthClient.verifyToken] Verifying current session', {
        url: this.endpoints.verifyToken,
        method: 'POST',
      });
    }
    try {
      const response = await this.http.post(this.endpoints.verifyToken, {});
      if (enableConsoleLogs.loginProcess) {
        console.log('[AuthClient.verifyToken] Session VALID', { url: this.endpoints.verifyToken, response });
      }
      return { success: true, data: { valid: true } };
    } catch (error) {
      if (enableConsoleLogs.loginProcess) {
        console.log('[AuthClient.verifyToken] Session INVALID / request failed — treating as not verified', {
          url: this.endpoints.verifyToken,
          error,
        });
      }
      return { success: false, data: { valid: false } };
    }
  }

  async changePassword(userId: number, currentPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    return this.http.post(this.endpoints.changePassword, {
      UserId: userId,
      CurrentPassword: currentPassword,
      NewPassword: newPassword,
    });
  }

  async forgotPassword(request: ForgotPasswordRequest): Promise<ApiResponse<{ message: string }>> {
    return this.http.post(this.endpoints.forgotPassword, request);
  }

  async resetPasswordComplete(request: ResetPasswordCompleteRequest): Promise<ApiResponse<{ message: string }>> {
    return this.http.post(this.endpoints.resetPassword, request);
  }

  async logout(refreshToken?: string): Promise<ApiResponse<void>> {
    return this.http.post(this.endpoints.logout, {
      RefreshToken: refreshToken || '',
    });
  }

  /**
   * Issues a single-use, ~30-second-TTL cross-app SSO handoff code standing in for the
   * caller's own current access + refresh token pair — see the backend's
   * IssueAuthHandoffCode action doc comment. Authenticated: relies on this.http already
   * having the caller's current access token set (via setToken()), same as verifyToken()
   * above. Called by the login app right before redirecting back to a consuming app, in
   * place of putting the raw JWT in the URL.
   *
   * Returns the UNWRAPPED `{ code }` shape directly, not `ApiResponse<...>` — HttpClient's
   * handleResponse() already strips the backend's `{success, data, errors}` GoWebResponse
   * envelope down to just `data` before this promise resolves (confirmed by reading
   * handleResponse: `if ('success' in data_parsed && 'data' in data_parsed) return
   * data_parsed.data as T`). A previous version of this method typed its generic as
   * `ApiResponse<IssueHandoffCodeResponse>`, which — since generics have no effect on the
   * actual runtime value — made every genuinely successful `{code: "..."}` response fail
   * the caller's `response.data?.code` check (there is no `.data` to read), silently
   * treating every successful handoff-code issuance as a failure. Confirmed via a live
   * browser replay + server access log cross-check: every real `/handoff/issue` call
   * returned HTTP 200, yet the frontend still reported "Failed to issue handoff code."
   * On failure, handleResponse throws (a non-2xx status, or `success: false` in the
   * envelope) rather than returning a falsy value — callers must catch/await-reject, not
   * check a `.success` field on the resolved value.
   */
  async issueHandoffCode(refreshToken: string, returnOrigin?: string): Promise<IssueHandoffCodeResponse> {
    if (enableConsoleLogs.loginProcess) {
      console.log('[AuthClient.issueHandoffCode] Requesting cross-app handoff code', {
        url: this.endpoints.issueHandoffCode,
        method: 'POST',
        hasRefreshToken: !!refreshToken,
      });
    }
    try {
      // `ReturnOrigin` (optional, S8) lets the server bind the code to the origin it is handed to
      // (PublicGateway:HandoffTenantBindingEnabled). Omitted entirely when not supplied, so the request
      // is byte-identical to the pre-S8 payload.
      const response = await this.http.post<IssueHandoffCodeResponse>(this.endpoints.issueHandoffCode, {
        RefreshToken: refreshToken,
        ...(returnOrigin ? { ReturnOrigin: returnOrigin } : {}),
      });
      if (enableConsoleLogs.loginProcess) {
        console.log('[AuthClient.issueHandoffCode] Response received', {
          url: this.endpoints.issueHandoffCode,
          hasCode: !!response?.code,
        });
      }
      return response;
    } catch (error) {
      if (enableConsoleLogs.loginProcess) {
        console.log('[AuthClient.issueHandoffCode] Request FAILED', { url: this.endpoints.issueHandoffCode, error });
      }
      throw error;
    }
  }

  /**
   * Redeems a handoff code (see issueHandoffCode above) for the real token pair it stands
   * in for. Unauthenticated — the caller has no token yet at this point.
   *
   * Returns the UNWRAPPED token-pair shape directly, not `ApiResponse<...>` — see
   * issueHandoffCode's doc comment above for why (same bug, same fix, applied here too).
   */
  async redeemHandoffCode(code: string): Promise<RedeemHandoffCodeResponse> {
    if (enableConsoleLogs.loginProcess) {
      console.log('[AuthClient.redeemHandoffCode] Redeeming cross-app handoff code', {
        url: this.endpoints.redeemHandoffCode,
        method: 'POST',
        codeLength: code?.length,
      });
    }
    try {
      const response = await this.http.post<RedeemHandoffCodeResponse>(this.endpoints.redeemHandoffCode, {
        Code: code,
      });
      if (enableConsoleLogs.loginProcess) {
        console.log('[AuthClient.redeemHandoffCode] Response received', {
          url: this.endpoints.redeemHandoffCode,
          hasAccessToken: !!response?.accessToken,
        });
      }
      return response;
    } catch (error) {
      if (enableConsoleLogs.loginProcess) {
        console.log('[AuthClient.redeemHandoffCode] Request FAILED — this is why a session can bounce back to sign-in', {
          url: this.endpoints.redeemHandoffCode,
          error,
        });
      }
      throw error;
    }
  }
}
